import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, Moon, Sun, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuraLogo from './AuraLogo';
import BetaSignupForm from './BetaSignupForm';
import LanguageSelector from './LanguageSelector';
import { trackCTAClick, trackMobileMenuToggle, trackThemeChange } from '../utils/analytics';
import { useTheme } from '../contexts/ThemeContext';

interface NavigationProps {
  className?: string;
}

const Navigation: React.FC<NavigationProps> = ({ className = '' }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showBetaSignup, setShowBetaSignup] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { theme, toggleTheme: toggleAppTheme } = useTheme();
  const isDarkMode = theme === 'dark';

  const isHomeHero = location.pathname === '/' && !isScrolled;

  const navItems = [
    { path: '/how-it-works', label: t('navigation.how_it_works') },
    { path: '/features', label: t('navigation.features') },
    { path: '/pricing', label: t('navigation.pricing') },
    { path: '/about', label: t('navigation.about') },
    { path: '/support', label: t('navigation.contact') },
  ];

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 36);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const toggleTheme = () => {
    const nextTheme = isDarkMode ? 'light' : 'dark';
    toggleAppTheme();
    trackThemeChange(nextTheme, 'manual');
  };

  const openBeta = (locationName: string) => {
    trackCTAClick('Join early access', locationName);
    setShowBetaSignup(true);
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <motion.nav
        initial={{ y: -70 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-50 border-b transition-colors duration-300 ${
          isHomeHero
            ? 'border-white/15 bg-transparent text-white'
            : 'border-black/10 bg-[#fffdf8]/95 text-[#171817] shadow-[0_1px_16px_rgba(0,0,0,0.04)] backdrop-blur-xl dark:border-white/10 dark:bg-[#171c27]/95 dark:text-white'
        } ${className}`}
        aria-label={t('navigation.main_aria')}
      >
        <div className="mx-auto flex h-[76px] w-[calc(100%_-_48px)] max-w-[1200px] items-center justify-between">
          <Link to="/" className="flex items-center text-current" aria-label={t('navigation.home_aria')}>
            <AuraLogo className="hidden min-[430px]:block" width={150} height={42} />
            <AuraLogo className="min-[430px]:hidden" width={42} height={42} compact />
          </Link>

          <div className="hidden items-center gap-7 lg:flex">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`border-b py-2 text-[13px] font-semibold transition-colors ${
                  location.pathname === item.path
                    ? 'border-[#c9a84c] text-[#c9a84c]'
                    : 'border-transparent text-current opacity-75 hover:opacity-100'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <div className={isHomeHero ? 'text-white' : ''}>
              <LanguageSelector />
            </div>
            <button
              onClick={toggleTheme}
              className={`grid h-9 w-9 place-items-center rounded-full border transition-colors ${
                isHomeHero
                  ? 'border-white/25 text-white hover:bg-white/10'
                  : 'border-black/10 text-current hover:bg-black/5 dark:border-white/15 dark:hover:bg-white/10'
              }`}
              aria-label={t('navigation.theme_aria')}
            >
              {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button
              onClick={() => openBeta('navigation_desktop')}
              className="hidden min-h-10 items-center bg-[#c9a84c] px-5 text-[13px] font-bold text-[#171817] transition-colors hover:bg-[#d7bb68] sm:inline-flex"
            >
              {t('navigation.join_early_access')}
            </button>
            <button
              onClick={() => {
                const next = !isMobileMenuOpen;
                setIsMobileMenuOpen(next);
                trackMobileMenuToggle(next ? 'open' : 'close');
              }}
              className={`grid h-10 w-10 place-items-center border lg:hidden ${
                isHomeHero ? 'border-white/25 text-white' : 'border-black/10 dark:border-white/15'
              }`}
              aria-label={t('navigation.menu_aria')}
              aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            className="fixed inset-x-0 top-[76px] z-40 border-b border-black/10 bg-[#fffdf8] px-6 py-7 text-[#171817] shadow-xl dark:border-white/10 dark:bg-[#171c27] dark:text-white lg:hidden"
          >
            <nav className="mx-auto grid max-w-xl">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className="border-b border-black/10 py-4 font-serif text-2xl"
                >
                  {item.label}
                </Link>
              ))}
              <button
                onClick={() => openBeta('navigation_mobile')}
                className="mt-6 min-h-12 bg-[#c9a84c] px-5 text-sm font-bold"
              >
                {t('navigation.join_early_access')}
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <BetaSignupForm isOpen={showBetaSignup} onClose={() => setShowBetaSignup(false)} />
    </>
  );
};

export default Navigation;
